import { useEffect, useState } from "react";
import { getPortfolio, createPortfolioItem, updatePortfolioItem, deletePortfolioItem, uploadSiteImage } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X, Camera, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const CATS = ["Imobiliário", "Consultoria", "Design"];
const empty = { title: "", category: CATS[0], description: "", image_url: "", featured: false };

export default function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  async function load() {
    const data = await getPortfolio();
    setItems(data || []);
  }
  useEffect(() => { load(); }, []);

  function startEdit(p) { setEditing(p.id); setForm({ title: p.title, category: p.category, description: p.description || "", image_url: p.image_url, featured: !!p.featured }); }
  function startNew() { setEditing("new"); setForm(empty); }
  function cancel() { setEditing(null); setForm(empty); }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadSiteImage(file, "portfolio");
      setForm(prev => ({ ...prev, image_url: url }));
      toast.success("Imagem carregada.");
    } catch (err) {
      console.error(err);
      toast.error("Falha ao carregar imagem.");
    } finally {
      setUploading(false);
    }
  }

  function handleRemoveImage() {
    setForm(prev => ({ ...prev, image_url: "" }));
  }

  async function save() {
    try {
      if (editing === "new") await createPortfolioItem(form);
      else await updatePortfolioItem(editing, form);
      toast.success("Salvo.");
      cancel(); load();
    } catch (err) { console.error(err); toast.error("Falha ao salvar."); }
  }

  async function remove() {
    if (!itemToDelete) return;
    try {
      await deletePortfolioItem(itemToDelete);
      toast.success("Removido."); 
      load();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover projeto.");
    } finally {
      setItemToDelete(null);
    }
  }

  return (
    <div data-testid="admin-portfolio-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl text-softblack">Portfólio</h2>
          <p className="text-warmgray text-sm font-light mt-1">Gerencie seus projetos e destaques.</p>
        </div>
        <button onClick={startNew} data-testid="portfolio-new" className="btn-primary !px-6 !py-3 !text-[11px] w-full sm:w-auto flex items-center justify-center">
          <Plus size={16} strokeWidth={1.5} className="mr-2" /> Novo Projeto
        </button>
      </div>

      {/* Form for creating new portfolio items (always at the top) */}
      {editing === "new" && (
        <div data-testid="portfolio-form" className="bg-white border border-fendi p-10 mb-12 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between mb-8 border-b border-fendi/30 pb-4">
            <p className="eyebrow !text-xs tracking-widest text-softblack/60">Novo Item de Portfólio</p>
            <button onClick={cancel} className="text-warmgray hover:text-softblack transition-colors"><X size={20} /></button>
          </div>
          
          <div className="space-y-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-1">
                <span className="eyebrow mb-3 block">Imagem</span>
                <div className="relative group w-full aspect-[4/3] bg-fendi/10 overflow-hidden border border-fendi/30 rounded-sm">
                  {form.image_url ? (
                    <>
                      <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button onClick={handleRemoveImage} className="bg-white/90 p-1.5 text-destructive hover:bg-white transition-colors shadow-sm">
                            <Trash2 size={14} />
                          </button>
                        </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-warmgray"><Camera size={24} strokeWidth={1} /></div>
                  )}
                  <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white">
                    {uploading ? <Loader2 className="animate-spin" /> : <Camera size={20} />}
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>
              </div>

              <div className="md:col-span-3 space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <Field label="Título do Projeto" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="portfolio-input-title" />
                  <div className="block">
                    <span className="eyebrow mb-3 block">Categoria</span>
                    <select data-testid="portfolio-input-category" className="editorial-field bg-transparent w-full" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="block">
                  <span className="eyebrow mb-3 block">Descrição Detalhada</span>
                  <textarea data-testid="portfolio-input-description" rows={4} className="editorial-field w-full" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>

                <label className="flex items-center gap-3 h-[52px] px-4 border border-fendi/20 bg-fendi/5 cursor-pointer hover:bg-fendi/10 transition-colors">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="portfolio-input-featured" className="accent-softblack" />
                  <span className="text-sm font-light text-softblack">Destacar na Home</span>
                </label>
              </div>
            </div>

            <div className="pt-6 border-t border-fendi/30 flex justify-end">
              <button onClick={save} data-testid="portfolio-save" className="btn-primary !px-10">Salvar Projeto</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((p) => (
          <div key={p.id} data-testid={`portfolio-card-${p.id}`} className="group bg-white border border-fendi hover:shadow-md transition-all duration-300 flex flex-col">
            {/* Edit form for this specific item */}
            {editing === p.id ? (
              <div className="p-6 bg-champagne/20 flex-grow animate-in fade-in duration-200">
                <div className="flex items-center justify-between mb-6 border-b border-fendi/20 pb-3">
                  <p className="eyebrow !text-[10px] text-softblack/50">Editando Projeto</p>
                  <button onClick={cancel} className="text-warmgray hover:text-softblack"><X size={16} /></button>
                </div>
                
                <div className="space-y-5">
                  <div className="relative group w-full h-32 bg-fendi/10 overflow-hidden border border-fendi/30 rounded-sm">
                    {form.image_url ? (
                      <>
                        <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button onClick={handleRemoveImage} className="bg-white/90 p-1 text-destructive hover:bg-white transition-colors shadow-sm">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-warmgray"><Camera size={20} strokeWidth={1} /></div>
                    )}
                    <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white">
                      {uploading ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />}
                      <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  </div>

                  <Field label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid={`portfolio-input-title-${p.id}`} />
                  
                  <div className="block">
                    <span className="eyebrow mb-2 block">Categoria</span>
                    <select data-testid={`portfolio-input-category-${p.id}`} className="editorial-field !py-2 bg-transparent w-full text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div className="block">
                    <span className="eyebrow mb-2 block">Descrição</span>
                    <textarea data-testid={`portfolio-input-description-${p.id}`} rows={3} className="editorial-field !py-2 w-full text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>

                  <label className="flex items-center gap-3 py-2 cursor-pointer">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid={`portfolio-input-featured-${p.id}`} className="accent-softblack" />
                    <span className="text-[11px] eyebrow text-softblack uppercase tracking-wider">Destaque</span>
                  </label>

                  <div className="pt-4">
                    <button onClick={save} data-testid={`portfolio-save-${p.id}`} className="btn-primary w-full !py-3 !text-[10px] shadow-sm">Confirmar Alterações</button>
                  </div>
                </div>
              </div>
            ) : (
              /* Display view for this item */
              <div className="flex flex-col h-full">
                <div className="relative overflow-hidden aspect-[4/3]">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full bg-fendi/5 flex items-center justify-center text-warmgray/30"><Camera size={40} strokeWidth={0.5} /></div>
                  )}
                  {p.featured && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-softblack text-white text-[9px] eyebrow px-3 py-1 tracking-[0.2em]">Destaque</span>
                    </div>
                  )}
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <p className="font-serif text-xl text-softblack line-clamp-1">{p.title}</p>
                      <span className="eyebrow shrink-0 !text-[9px] text-warmgray">{p.category}</span>
                    </div>
                    {p.description && <p className="text-warmgray text-sm font-light leading-relaxed line-clamp-2 mb-4">{p.description}</p>}
                  </div>
                  
                  <div className="pt-4 border-t border-fendi/10 flex items-center justify-end gap-4">
                    <button onClick={() => startEdit(p)} data-testid={`portfolio-edit-${p.id}`} className="text-warmgray hover:text-softblack transition-colors flex items-center gap-2">
                      <Pencil size={14} strokeWidth={1.5} />
                      <span className="eyebrow !text-[9px]">Editar</span>
                    </button>
                    <button onClick={() => setItemToDelete(p.id)} data-testid={`portfolio-delete-${p.id}`} className="text-warmgray hover:text-destructive transition-colors flex items-center gap-2">
                      <Trash2 size={14} strokeWidth={1.5} />
                      <span className="eyebrow !text-[9px]">Excluir</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-warmgray font-light py-20 bg-fendi/5 border border-dashed border-fendi">Nenhum proyecto cadastrado.</div>}
      </div>

      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Excluir Projeto?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente o projeto do seu portfólio. Você não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="eyebrow mb-3 block">{label}</span>
      <input data-testid={testid} className="editorial-field w-full" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
